import { type FormEvent, type ReactElement, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "ky";

import { register } from "@/api/auth";
import { Btn2 } from "@/components/hifi/Btn2";
import { Pill } from "@/components/hifi/Pill";
import { useAuthStore } from "@/stores/auth";

const MIN_PASSWORD = 12;

export function RegisterPage(): ReactElement {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    organization_name: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const bind =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
    };

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (form.password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${String(MIN_PASSWORD)} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await register(form);
      setSession(res.user, res.token.access_token);
      await navigate({ to: "/" });
    } catch (err) {
      if (err instanceof HTTPError && err.response.status === 409) {
        setError("That email is already registered.");
      } else if (err instanceof HTTPError && err.response.status === 403) {
        setError("Self-registration is disabled.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "mt-1 block w-full rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none";

  return (
    <main className="flex min-h-screen items-center justify-center bg-n-75 p-6 font-sans text-n-900">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-2.5">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-2 text-[16px] font-bold text-white shadow-2"
            style={{
              background: "linear-gradient(135deg, var(--a-500), var(--a-700))",
            }}
          >
            C
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-[-0.3px]">
              CSMP Risk Manager
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3px] text-n-500">
              Physical Security Risk Assessment
            </div>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="rounded-3 border border-n-150 bg-white p-6 shadow-2"
          noValidate
        >
          <div className="mb-1 flex items-center justify-between">
            <h1 className="text-[16px] font-semibold text-n-900">
              Create an account
            </h1>
            <Pill variant="accent">Workspace</Pill>
          </div>
          <p className="text-[12px] text-n-500">
            Spin up a new workspace as the Owner.
          </p>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <label className="block text-[11px] font-medium text-n-600">
              First name
              <input
                value={form.first_name}
                onChange={bind("first_name")}
                required
                className={inputCls}
              />
            </label>
            <label className="block text-[11px] font-medium text-n-600">
              Last name
              <input
                value={form.last_name}
                onChange={bind("last_name")}
                required
                className={inputCls}
              />
            </label>
          </div>

          <label className="mt-4 block text-[11px] font-medium text-n-600">
            Organization
            <input
              value={form.organization_name}
              onChange={bind("organization_name")}
              required
              className={inputCls}
            />
          </label>

          <label className="mt-4 block text-[11px] font-medium text-n-600">
            Email
            <input
              type="email"
              value={form.email}
              onChange={bind("email")}
              required
              autoComplete="email"
              className={inputCls}
            />
          </label>

          <label className="mt-4 block text-[11px] font-medium text-n-600">
            Password
            <input
              type="password"
              value={form.password}
              onChange={bind("password")}
              required
              minLength={MIN_PASSWORD}
              autoComplete="new-password"
              className={inputCls}
            />
          </label>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
            >
              {error}
            </div>
          )}

          <div className="mt-5">
            <Btn2
              variant="accent"
              size="lg"
              full
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Creating…" : "Create account"}
            </Btn2>
          </div>

          <p className="mt-4 text-[11px] text-n-500">
            Already have an account?{" "}
            <a
              href="/login"
              className="font-medium text-a-600 no-underline hover:underline"
            >
              Sign in
            </a>
          </p>
        </form>
      </div>
    </main>
  );
}
